<script lang="ts" setup>
import { generateId } from '#helpers';

const props = defineProps({
  id: { type: String, default: '' },
  label: { type: String, required: false, default: '' },
  checked: { type: Boolean, default: false },
  indeterminate: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  fullWidth: { type: Boolean, default: true },
  testId: { type: String, required: false, default: undefined }
});

const uuid = generateId(`checkbox-${props.id}-`);

const emit = defineEmits<{
  change: [value: boolean];
}>();
</script>

<template>
  <label
    class="flex items-center h-5 text-md"
    :class="{ 'hover:cursor-pointer': !props.disabled, 'w-full': props.fullWidth }"
  >
    <input
      :id="uuid"
      class="peer indeterminate:bg-primary indeterminate:disabled:bg-default-very-light hover:indeterminate:bg-primary-dark focus:indeterminate:bg-primary-darker disabled:checked:bg-default-very-light checked:disabled:fill-amber-500 focus:indeterminate:outline-primary-darker h-5 w-5 text-primary rounded border border-default-neutral-217 disabled:border-gray-200 hover:border-default-disabled focus:outline-1 focus:border-none focus:outline-offset-0 focus:ring-0 focus:ring-transparent focus:outline-primary transition duration-150 ease-in-out"
      type="checkbox"
      :checked="checked"
      :indeterminate="indeterminate"
      :disabled="disabled"
      :data-testid="testId"
      @change="emit('change', ($event.target as HTMLInputElement)?.checked || false)"
    />
    <div class="ml-2.5 text-sm peer-disabled:text-default-lighter text-default">
      {{ label }}
      <span v-if="$slots.default" class="text-gray-500 block text-xs"> <slot /></span>
    </div>
  </label>
</template>

<style scoped>
[type='checkbox']:checked:disabled {
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 16 16' fill='%23929292' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M12.207 4.793a1 1 0 010 1.414l-5 5a1 1 0 01-1.414 0l-2-2a1 1 0 011.414-1.414L6.5 9.086l4.293-4.293a1 1 0 011.414 0z'/%3E%3C/svg%3E");
  @apply hover:bg-default-very-light;
}

[type='checkbox']:indeterminate {
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='10' viewBox='0 0 10 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='3' y='3' width='4' height='4' rx='1.5' fill='white'/%3E%3Crect x='3' y='3' width='4' height='4' rx='1.5'/%3E%3C/svg%3E");
}

[type='checkbox']:indeterminate:disabled {
  background-image: url("data:image/svg+xml,%3Csvg width='10' height='10' viewBox='0 0 10 10' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Crect x='3' y='3' width='4' height='4' rx='1.5' fill='%23929292'/%3E%3Crect x='3' y='3' width='4' height='4' rx='1.5'/%3E%3C/svg%3E");
}
</style>
